// lib/topaz-image.ts
import { calculateSeedVR2UpscaleFactor, normalizeSeedVR2InputMegaPixels, SEEDVR2_INPUT_PIXELS, SEEDVR2_OUTPUT_PIXELS } from '#lib/seedvr2';

export const hasTopazImageResolutionContract = (params: unknown): boolean => {
  if (!params || typeof params !== 'object') return false;
  const source = params as Record<string, unknown>;
  return source.inputMegaPixels === 'auto'
    || source.inputMegaPixels === 0.5
    || source.inputMegaPixels === 1
    || source.inputMegaPixels === 2
    || source.inputMegaPixels === 5
    || source.inputMegaPixels === 10
    || source.inputMegaPixels === 16
    || source.outputMegaPixels === 16
    || source.outputMegaPixels === 30
    || source.outputMegaPixels === 40;
};

export const getTopazUpscaleFactorFromParams = (
  params: unknown,
  targetWidth?: number,
  targetHeight?: number,
): number => {
  if (!params || typeof params !== 'object') return 2;
  const source = params as Record<string, unknown>;
  const hasResolutionContract = hasTopazImageResolutionContract(source);

  if (hasResolutionContract) {
    const input = normalizeSeedVR2InputMegaPixels(source.inputMegaPixels);
    const inputPixels = SEEDVR2_INPUT_PIXELS[input === 'auto' ? 16 : input];
    const outputPixels = source.outputMegaPixels === 16 || source.outputMegaPixels === 30 || source.outputMegaPixels === 40
      ? SEEDVR2_OUTPUT_PIXELS[source.outputMegaPixels]
      : SEEDVR2_OUTPUT_PIXELS[40];
    const sourceWidth =
      typeof targetWidth === 'number' && Number.isFinite(targetWidth) && targetWidth > 0
        ? targetWidth
        : (typeof source.topazInputWidth === 'number' && Number.isFinite(source.topazInputWidth) && source.topazInputWidth > 0
          ? source.topazInputWidth
          : null);
    const sourceHeight =
      typeof targetHeight === 'number' && Number.isFinite(targetHeight) && targetHeight > 0
        ? targetHeight
        : (typeof source.topazInputHeight === 'number' && Number.isFinite(source.topazInputHeight) && source.topazInputHeight > 0
          ? source.topazInputHeight
          : null);
    const hasSourceDimensions = sourceWidth !== null && sourceHeight !== null;
    const factor = hasSourceDimensions
      ? calculateSeedVR2UpscaleFactor({ sourceWidth: sourceWidth!, sourceHeight: sourceHeight!, targetPixels: outputPixels })
      : calculateSeedVR2UpscaleFactor({
        sourceWidth: Math.sqrt(inputPixels),
        sourceHeight: Math.sqrt(inputPixels),
        targetPixels: outputPixels,
      });
    return Math.max(1, Math.min(4, factor));
  }

  const directUpscale =
    (typeof source.upscale_factor === 'number' && Number.isFinite(source.upscale_factor))
      ? source.upscale_factor
      : (typeof source.upscaleFactor === 'number' && Number.isFinite(source.upscaleFactor))
        ? source.upscaleFactor
        : null;
  const legacyOutputMp =
    (typeof source.topazOutputMegaPixels === 'number' && Number.isFinite(source.topazOutputMegaPixels))
      ? source.topazOutputMegaPixels
      : (typeof source.outputMegaPixels === 'number' && Number.isFinite(source.outputMegaPixels))
        ? source.outputMegaPixels
        : (typeof source.output_megapixels === 'number' && Number.isFinite(source.output_megapixels))
          ? source.output_megapixels
          : null;

  const sourcePixels =
    typeof targetWidth === 'number' &&
    Number.isFinite(targetWidth) &&
    targetWidth > 0 &&
    typeof targetHeight === 'number' &&
    Number.isFinite(targetHeight) &&
    targetHeight > 0
      ? targetWidth * targetHeight
      : null;

  const inferredUpscale = (() => {
    if (directUpscale !== null) return directUpscale;
    if (legacyOutputMp !== null && sourcePixels && sourcePixels > 0) {
      return Math.sqrt((legacyOutputMp * 1_000_000) / sourcePixels);
    }
    return 2;
  })();
  return Math.max(1, Math.min(4, Math.round(inferredUpscale * 10) / 10));
};
